import React from 'react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { clsx } from 'clsx';
import type { GreenBeanProfileFormValues } from './useGreenBeanProfileForm';
import { useLanguage } from '../../features/language/useLanguage';

const COLORS = [
  'Blue Green',
  'Bluish-Green',
  'Green',
  'Greenish',
  'Yellow-Green',
  'Pale Yellow',
  'Yellowish',
  'Brownish',
];

const GRADES = ['1', '2', '3', '4', '5'];

const AROMAS = [
  'Grassy / Herbal',
  'Vegetal',
  'Sweet / Hay-like',
  'Fruity',
  'Nutty',
  'Musty / Mouldy',
  'Earthy / Dirty',
  'Fermented / Sour',
  'Baggy',
  'Chemical / Phenolic',
  'Smoky',
];

interface GreenBeanPhysicalSectionProps {
  control: Control<GreenBeanProfileFormValues>;
  register: UseFormRegister<GreenBeanProfileFormValues>;
  errors: FieldErrors<GreenBeanProfileFormValues>;
}

export const GreenBeanPhysicalSection: React.FC<GreenBeanPhysicalSectionProps> = ({
  control,
  register,
  errors,
}) => {
  const { t } = useLanguage();
  return (
  <div className="space-y-4">
    <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('greenBean.physical.title')}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('greenBean.quality.grade')}</label>

        <Controller
          control={control}
          name="grade"
          rules={{ required: t('greenBean.quality.errors.gradeRequired') }}
          render={({ field }) => (
            <select
              {...field}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              data-test-id="green-bean-grade-select"
            >
              <option value="">{t('greenBean.physical.selectGrade')}</option>
              {GRADES.map((grade) => (
                <option key={grade} value={grade}>
                  {t('greenBean.quality.gradeLabel', { grade })}
                </option>
              ))}
            </select>
          )}
        />

        {errors.grade && <p className="text-red-500 text-xs mt-1">{errors.grade.message}</p>}
      </div>
      <div>
        <Controller
          control={control}
          name="color"
          rules={{ required: t('greenBean.quality.errors.colorRequired') }}
          render={({ field }) => (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">{t('greenBean.quality.color')}</label>

              <select
                {...field}
                className={clsx(
                  'h-10 w-full rounded-lg border px-3 text-sm bg-white focus:outline-none focus:ring-1',
                  errors.color
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-black focus:border-black'
                )}
              >
                <option value="">{t('greenBean.physical.selectColor')}</option>
                {COLORS.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </select>

              {errors.color && <p className="text-xs text-red-500">{errors.color.message}</p>}
            </div>
          )}
        />
      </div>

      <div className="-mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 md:col-span-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.quality.moistureContent')}</label>
          <input
            type="number"
            step="0.01"
            {...register('moisture_content', { required: t('greenBean.physical.errors.moistureRequired'), valueAsNumber: true })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            data-test-id="green-bean-moisture-input"
          />
          {errors.moisture_content && (
            <p className="text-red-500 text-xs mt-1">{errors.moisture_content.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('greenBean.physical.density')}</label>

          <input
            type="number"
            step="0.1"
            {...register('density', { valueAsNumber: true })}
            placeholder={t('greenBean.physical.densityPlaceholder')}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            data-test-id="green-bean-density-input"
          />

          {errors.density && <p className="text-red-500 text-xs mt-1">{errors.density.message}</p>}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('greenBean.quality.aromaMulti')}</label>
        <Controller
          control={control}
          name="aroma"
          render={({ field }) => {
            const selectedAromas = Array.isArray(field.value) ? field.value : [];

            const toggleAroma = (aroma: string) => {
              const newAromas = selectedAromas.includes(aroma)
                ? selectedAromas.filter((a) => a !== aroma)
                : [...selectedAromas, aroma];
              field.onChange(newAromas);
            };

            return (
              <div className="flex flex-wrap gap-2">
                {AROMAS.map((aroma) => (
                  <button
                    key={aroma}
                    type="button"
                    onClick={() => toggleAroma(aroma)}
                    data-test-id={`green-bean-aroma-button-${aroma}`}
                    className={clsx(
                      'px-3 py-1.5 rounded-full text-sm font-medium transition-colors border',
                      selectedAromas.includes(aroma)
                        ? 'bg-primary-100 text-primary-800 border-primary-200'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    {aroma}
                  </button>
                ))}
              </div>
            );
          }}
        />
      </div>
    </div>
  </div>
  );
};
